import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { CollectionProgramAsset, isLiveProgram } from '@kaltura-ott/tvpil-shared';

import { useCardRef, useMouseEnterHandle, useMouseLeaveHandle } from 'components/presenters/Card/hooks';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider/RailProvider';
import { WidgetProviderSettings } from 'context/widgetProvider/types';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';
import { useParentalPinStore, useRecordRepository } from 'hooks';
import { getAriaLabelForAsset, getSeasonEpisodeTitleV2 } from 'utils';

import { useSeriesCatchUpCardChannelInfo } from '../useSeriesCatchUpCardChannelInfo/useSeriesCatchUpCardChannelInfo';
import { useSeriesCatchUpCardImages } from '../useSeriesCatchUpCardImages/useSeriesCatchUpCardImages';
import { useSeriesCatchUpCardInfoClickHandler } from '../useSeriesCatchUpCardInfoClickHandler/useSeriesCatchUpCardInfoClickHandler';
import { useSeriesCatchUpCardLink } from '../useSeriesCatchUpCardLink/useSeriesCatchUpCardLink';
import { useSeriesCatchUpCardMainProps } from '../useSeriesCatchUpCardMainProps/useSeriesCatchUpCardMainProps';
import { useSeriesCatchUpCardRating } from '../useSeriesCatchUpCardRating/useSeriesCatchUpCardRating';
import { useSeriesCatchUpClickHandler } from '../useSeriesCatchUpClickHandler/useSeriesCatchUpClickHandler';

interface Props {
  seriesCatchup: CollectionProgramAsset;
  itemWidth: number;
  imageHeight: number;
}
// TODO: need to cover by units
function useSeriesCatchUpCard({ seriesCatchup, itemWidth, imageHeight }: Props) {
  const intl = useIntl();
  const parentalPinStore = useParentalPinStore();
  const { onHover, nextButtons } = useRailContext();
  const {
    settings: { shouldShowRating, imageType, shouldShowEntitlementIndicator },
    type,
    isGrid,
  } = useWidgetContext<CollectionProgramAsset, WidgetProviderSettings>();

  const {
    isTypeOfSeries,
    isPurchasable,
    isLinkAsAsset,
    isCatchupSeries,
    isChannelAsset,
    isSingleVod,
    isProgramAsset,
    isCollectionEpisode,
    isCollectionInAppAdultLink,
    adult,
  } = useSeriesCatchUpCardMainProps({
    data: seriesCatchup,
    isGrouped: true,
  });

  const isAdult = Boolean(adult);
  const shouldDisplayCartIcon = isPurchasable && Boolean(shouldShowEntitlementIndicator);
  const isProgramLive = isLiveProgram(seriesCatchup);
  const { link } = useSeriesCatchUpCardLink({
    data: seriesCatchup,
    isSingleVod,
    isFutureProgram: false,
    collectionType: type,
    isChannelAsset,
    isProgramLive,
  });

  const isAdultPinNotEntered = !parentalPinStore.isAdultPinEntered;
  const isAdultBlocked = isAdultPinNotEntered && isAdult;
  const isRestricted = !!((isAdult || isCollectionInAppAdultLink) && isAdultPinNotEntered);

  // refs
  const handleLinkRef = useRef(null);
  const { ref, handleRef } = useCardRef(itemWidth, imageHeight);
  // handlers
  const onMouseEnter = useMouseEnterHandle({ ref, itemWidth, imageHeight, isGrid, onHover, nextButtons });
  const onMouseLeave = useMouseLeaveHandle(onHover);

  const clickHandler = useSeriesCatchUpClickHandler({
    data: seriesCatchup,
    link,
    mediaTypeParam: seriesCatchup.type,
    isLinkAsAsset,
    isPurchasable,
    collectionType: type,
    isProgramAsset,
    isTypeOfSeries,
    isCatchupSeries,
    isCollectionEpisode,
    isCollectionInAppAdultLink,
    isRestricted,
    isAdultBlocked,
    parentalPinStore
  });
  const { handleClickInfo } = useSeriesCatchUpCardInfoClickHandler({
    id: seriesCatchup?.id!,
    externalId: undefined,
    mediaTypeParam: seriesCatchup.type,
    isChannelHasNoProgram: false,
    seriesId: seriesCatchup?.seriesId,
  });
  // rating
  const { isDisplayRating, assetRating } = useSeriesCatchUpCardRating({ shouldShowRating, item: seriesCatchup });
  // images urls
  const { backgroundImageUrl, defaultImageUrl } = useSeriesCatchUpCardImages({
    item: seriesCatchup,
    imageType,
    imageWidth: itemWidth,
    imageHeight,
  });
  // title and channelName
  const { title, channelName } = useSeriesCatchUpCardChannelInfo(seriesCatchup);
  // checking for info button presentation
  const channelHasProgram = Boolean(!isChannelAsset && !isLinkAsAsset);

  // season / episode title definition
  let seasonEpisodeTitle = '';

  const isEpisodeInfo = (isProgramAsset || isCollectionEpisode) && !isCatchupSeries;
  if (isEpisodeInfo) {
    seasonEpisodeTitle = getSeasonEpisodeTitleV2(intl, seriesCatchup, ':', true);
  }

  // seriesId definition
  const seriesId = seriesCatchup.seriesId!;

  // For record indication
  const recordRepository = useRecordRepository();
  const record = seriesCatchup?.id ? recordRepository.records.get(seriesCatchup.id.toString()) : null;
  const seriesRecord = seriesId ? recordRepository.seriesRecords.get(seriesId) : null;
  const isRecord = Boolean(record || seriesRecord);
  const isSeriesRecord = Boolean(seriesRecord);
  //

  // getting aria-label text
  const ariaLabel = getAriaLabelForAsset({
    intl,
    title,
    isRestricted,
    isPurchaseContent: shouldDisplayCartIcon,
    isRecord,
    isSeriesRecord,
    shouldPronouncedAiringTime: false
  });

  return {
    link,
    title,
    isAdult,
    isRecord,
    handleRef,
    ariaLabel,
    assetRating,
    channelName,
    clickHandler,
    onMouseEnter,
    onMouseLeave,
    isLinkAsAsset,
    handleLinkRef,
    isSeriesRecord,
    handleClickInfo,
    isDisplayRating,
    defaultImageUrl,
    channelHasProgram,
    backgroundImageUrl,
    seasonEpisodeTitle,
    shouldDisplayCartIcon,
  };
}

export { useSeriesCatchUpCard };
